import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { DatePicker } from '../components/ui/DatePicker';
import { StatCard } from '../components/ui/StatCard';
import { TargetProgressBar } from '../components/ui/TargetProgressBar';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { WorkloadCard } from '../components/operations/WorkloadCard';
import { ActivityDetailModal } from '../components/operations/ActivityDetailModal';
import { OperativeActivity, operativeActivitiesStore } from '../services/operativeActivities';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Users, AlertCircle, Clock, CheckCircle2, Receipt, Calendar, Download, Target, FileText, PieChart, Euro, Plus, Edit, Trash2, Eye } from 'lucide-react';
import { LineChart, Line, BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell, AreaChart, Area, ComposedChart } from 'recharts';
type TabType = 'operativa' | 'commerciale' | 'economica' | 'obiettivi';
// Mock Data for Charts
const trendData = [{
  name: 'Gen',
  offerte: 40000,
  positivi: 24000,
  targetOfferte: 35000,
  targetPositivi: 20000
}, {
  name: 'Feb',
  offerte: 30000,
  positivi: 13980,
  targetOfferte: 35000,
  targetPositivi: 18000
}, {
  name: 'Mar',
  offerte: 20000,
  positivi: 9800,
  targetOfferte: 30000,
  targetPositivi: 15000
}, {
  name: 'Apr',
  offerte: 27800,
  positivi: 3908,
  targetOfferte: 30000,
  targetPositivi: 15000
}, {
  name: 'Mag',
  offerte: 18900,
  positivi: 4800,
  targetOfferte: 25000,
  targetPositivi: 12000
}, {
  name: 'Giu',
  offerte: 23900,
  positivi: 3800,
  targetOfferte: 25000,
  targetPositivi: 12000
}];
const salesRepData = [{
  name: 'Alessio',
  positivi: 12000,
  target: 10000
}, {
  name: 'Lorenzo',
  positivi: 15000,
  target: 12000
}, {
  name: 'Gian Franco',
  positivi: 8000,
  target: 10000
}, {
  name: 'Tiziano',
  positivi: 9500,
  target: 9000
}];
const conversionData = [{
  name: 'Positivi',
  value: 29
}, {
  name: 'Negativi',
  value: 7
}, {
  name: 'In valutazione',
  value: 9
}];
const COLORS = ['#10B981', '#EF4444', '#F59E0B'];
// Target distribution of the offers (same order as conversionData)
const conversionTargetData = [{
  name: 'Obiettivo · Positivi',
  value: 34
}, {
  name: 'Obiettivo · Negativi',
  value: 4
}, {
  name: 'Obiettivo · In valutazione',
  value: 7
}];
// Target shares of the collections analysis (percent of total)
const collectionTargets = {
  incassato: 80,
  inScadenza: 15,
  scaduto: 5
};
// Opacity used for every target series, so real vs target reads at a glance
const TARGET_OPACITY = 0.35;
const revenueData = [{
  name: 'Gen',
  fatturato: 45000,
  proformato: 12000,
  target: 50000
}, {
  name: 'Feb',
  fatturato: 52000,
  proformato: 8000,
  target: 50000
}, {
  name: 'Mar',
  fatturato: 48000,
  proformato: 15000,
  target: 50000
}, {
  name: 'Apr',
  fatturato: 61000,
  proformato: 5000,
  target: 55000
}, {
  name: 'Mag',
  fatturato: 55000,
  proformato: 10000,
  target: 55000
}, {
  name: 'Giu',
  fatturato: 59000,
  proformato: 12000,
  target: 60000
}];
const objectivesData = [{
  id: 1,
  month: 'Gennaio',
  targetSales: 10000,
  actualSales: 12500,
  targetRevenue: 40000,
  actualRevenue: 38000
}, {
  id: 2,
  month: 'Febbraio',
  targetSales: 10000,
  actualSales: 9800,
  targetRevenue: 40000,
  actualRevenue: 41200
}, {
  id: 3,
  month: 'Marzo',
  targetSales: 12000,
  actualSales: 15000,
  targetRevenue: 45000,
  actualRevenue: 48000
}, {
  id: 4,
  month: 'Aprile',
  targetSales: 12000,
  actualSales: 11000,
  targetRevenue: 45000,
  actualRevenue: 42000
}, {
  id: 5,
  month: 'Maggio',
  targetSales: 15000,
  actualSales: 18000,
  targetRevenue: 50000,
  actualRevenue: 52000
}, {
  id: 6,
  month: 'Giugno',
  targetSales: 15000,
  actualSales: 16500,
  targetRevenue: 50000,
  actualRevenue: 51000
}];
const topClientsData = [{
  name: 'Metalmeccanica Rossi SRL',
  amount: '€ 25.400',
  perc: '8.5%'
}, {
  name: 'Edilizia Moderna SPA',
  amount: '€ 18.200',
  perc: '6.1%'
}, {
  name: 'Supermercati Market',
  amount: '€ 15.100',
  perc: '5.0%'
}, {
  name: 'Ristorante Da Mario',
  amount: '€ 12.800',
  perc: '4.2%'
}, {
  name: 'Studio Legale Bianchi',
  amount: '€ 9.500',
  perc: '3.1%'
}, {
  name: 'Autofficina Veloce',
  amount: '€ 8.200',
  perc: '2.7%'
}, {
  name: 'Bar Centrale',
  amount: '€ 7.100',
  perc: '2.4%'
}, {
  name: 'Farmacia Salute',
  amount: '€ 6.800',
  perc: '2.3%'
}, {
  name: 'Hotel Bellavista',
  amount: '€ 5.900',
  perc: '2.0%'
}, {
  name: 'Panificio Dolce',
  amount: '€ 4.500',
  perc: '1.5%'
}];
const allActivities = [{
  user: 'Alessio',
  action: 'ha creato un nuovo contratto',
  target: 'Metalmeccanica Rossi SRL',
  date: '2 ore fa',
  icon: CheckCircle2,
  bg: 'bg-green-100',
  text: 'text-green-600'
}, {
  user: 'Lorenzo',
  action: 'ha completato il sopralluogo',
  target: 'Ristorante Da Mario',
  date: '4 ore fa',
  icon: CheckCircle2,
  bg: 'bg-blue-100',
  text: 'text-blue-600'
}, {
  user: 'Tiziano',
  action: 'ha segnalato una non conformità',
  target: 'Edilizia Moderna',
  date: 'Ieri',
  icon: AlertCircle,
  bg: 'bg-red-100',
  text: 'text-red-600'
}, {
  user: 'Amministrazione',
  action: 'ha emesso fattura #2024/45',
  target: 'Studio Legale Bianchi',
  date: 'Ieri',
  icon: Receipt,
  bg: 'bg-yellow-100',
  text: 'text-yellow-600'
}, {
  user: 'Gian Franco',
  action: 'ha aggiornato il DVR',
  target: 'Supermercati Market',
  date: '2 giorni fa',
  icon: FileText,
  bg: 'bg-purple-100',
  text: 'text-purple-600'
}, {
  user: 'Alessio',
  action: 'ha pianificato corso formazione',
  target: 'Hotel Bellavista',
  date: '3 giorni fa',
  icon: Calendar,
  bg: 'bg-indigo-100',
  text: 'text-indigo-600'
}];
const deadlinesData = [{
  id: 1,
  title: 'Rinnovo DVR',
  client: 'Autofficina Veloce',
  date: 'Domani',
  priority: 'high'
}, {
  id: 2,
  title: 'Corso antincendio',
  client: 'Supermercato Market',
  date: '15/05/2024',
  priority: 'medium'
}, {
  id: 3,
  title: 'Visita medica Rossi Mario',
  client: 'Edilizia Moderna',
  date: '16/05/2024',
  priority: 'medium'
}, {
  id: 4,
  title: 'Scadenza fattura #44',
  client: 'Bar Centrale',
  date: '18/05/2024',
  priority: 'low'
}];
const columnsConfig = [{
  key: 'month',
  label: 'Mese'
}, {
  key: 'targetSales',
  label: 'Obiettivo\nofferte'
}, {
  key: 'actualSales',
  label: 'Reale\nofferte'
}, {
  key: 'diffSales',
  label: 'Differenza\nofferte'
}, {
  key: 'targetRevenue',
  label: 'Obiettivo\nfatturato'
}, {
  key: 'actualRevenue',
  label: 'Reale\nfatturato'
}, {
  key: 'diffRevenue',
  label: 'Differenza\nfatturato'
}];
export function Dashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('operativa');
  const [period, setPeriod] = useState('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showActivitiesModal, setShowActivitiesModal] = useState(false);
  const [showTopClientsModal, setShowTopClientsModal] = useState(false);
  const [showObjectivesModal, setShowObjectivesModal] = useState(false);
  const [showDeadlineViewModal, setShowDeadlineViewModal] = useState(false);
  const [showDeadlineEditModal, setShowDeadlineEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedDeadline, setSelectedDeadline] = useState<any>(null);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [objectivePeriodType, setObjectivePeriodType] = useState<'monthly' | 'annual'>('monthly');
  // Activity detail modal state
  const [selectedActivity, setSelectedActivity] = useState<OperativeActivity | null>(null);
  const [showActivityDetail, setShowActivityDetail] = useState(false);
  const userName = 'Alessio';
  const tabs = [{
    id: 'operativa' as TabType,
    label: 'Operativa'
  }, {
    id: 'commerciale' as TabType,
    label: 'Commerciale'
  }, {
    id: 'economica' as TabType,
    label: 'Economica'
  }, {
    id: 'obiettivi' as TabType,
    label: 'Obiettivi'
  }];
  const formatCurrency = (val: number) => `€ ${val.toLocaleString('it-IT')}`;
  const handleDeadlineClick = (item: any) => {
    setSelectedDeadline(item);
    setShowDeadlineViewModal(true);
  };
  const handleDeadlineEdit = (item: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDeadline(item);
    setShowDeadlineEditModal(true);
  };
  const handleDeadlineDelete = (item: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDeadline(item);
    setShowDeleteConfirm(true);
  };
  const handleViewActivity = (activity: OperativeActivity) => {
    setSelectedActivity(activity);
    setShowActivityDetail(true);
  };
  const handleEditActivity = (activity: OperativeActivity) => {
    navigate('/operations', {
      state: {
        editActivityId: activity.id
      }
    });
  };
  const handleStepComplete = (activityId: string, stepIndex: number) => {
    operativeActivitiesStore.completeStep(activityId, stepIndex);
    const updated = operativeActivitiesStore.getById(activityId);
    if (updated) {
      setSelectedActivity(updated);
    }
  };
  const renderOperativa = () => <>
      {/* KPI Cards - Static */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8" data-id="element-3994" data-mp="127rvhubzry8z">
        {[{
        name: 'Fatturato mensile',
        value: '€ 45.231',
        change: '+12%',
        changeType: 'positive',
        icon: TrendingUp,
        color: 'bg-blue-500'
      }, {
        name: 'Clienti attivi',
        value: '124',
        change: '+4',
        changeType: 'positive',
        icon: Users,
        color: 'bg-green-500'
      }, {
        name: 'Fatture in scadenza',
        value: '8',
        change: '€ 12.400',
        changeType: 'negative',
        icon: AlertCircle,
        color: 'bg-red-500'
      }, {
        name: 'Scadenze settimana',
        value: '15',
        change: 'Urgenti: 3',
        changeType: 'neutral',
        icon: Clock,
        color: 'bg-seform-orange'
      }].map(kpi => <div key={kpi.name} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 relative overflow-hidden group" data-id="element-3995" data-mp="3tdr8b4p6esnv">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-white/0 to-slate-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110" data-id="element-3996" data-mp="3kct2qhz3ad36"></div>
            <div className="relative z-10" data-id="element-3997" data-mp="1ikwrl9gu358x">
              <div className="flex justify-between items-start mb-4" data-id="element-3998" data-mp="1ijlo7aravzic">
                <div className={`p-3 rounded-xl ${kpi.color} bg-opacity-10`} data-id="element-3999" data-mp="1dmbsjddbirm2">
                  <kpi.icon className={`h-6 w-6 ${kpi.name === 'Scadenze settimana' ? 'text-seform-orange' : ''} ${kpi.name === 'Fatturato mensile' ? 'text-blue-600' : ''} ${kpi.name === 'Clienti attivi' ? 'text-green-600' : ''} ${kpi.name === 'Fatture in scadenza' ? 'text-red-600' : ''}`} data-id="element-4000" data-mp="380yzxobla3b4" />
                </div>
                <Badge variant={kpi.changeType === 'positive' ? 'success' : kpi.changeType === 'negative' ? 'danger' : 'neutral'} size="sm" data-id="element-4001" data-mp="332p55ets14q1">
                  {kpi.change}
                </Badge>
              </div>
              <h3 className="text-slate-500 text-sm font-medium" data-id="element-4002" data-mp="3lr2kmautloyg">{kpi.name}</h3>
              <p className="text-2xl font-bold text-seform-navy mt-1" data-id="element-4003" data-mp="25dvrg0uxootc">
                {kpi.value}
              </p>
            </div>
          </div>)}
      </div>

      {/* Carico di lavoro - Full width above activity/deadlines */}
      <div className="mb-8" data-id="element-4004" data-mp="nh0v5a9qu4mc">
        <WorkloadCard data-id="element-4005" data-mp="nhtsxlqq5jsc" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-4006" data-mp="ngit1x0egw6i">
        {/* Recent Activity */}
        <Card title="Attività recenti" description="Ultime operazioni registrate nel sistema" actions={<Button variant="light" size="sm" onClick={() => setShowActivitiesModal(true)} data-id="element-4008" data-mp="9pndy2zb5iuh">
              Vedi tutte
            </Button>} data-id="element-4007" data-mp="11ga0fewevebj">
          <div className="flow-root" data-id="element-4009" data-mp="ihpd7xxn4t36">
            <ul className="-mb-8" data-id="element-4010" data-mp="iiqyev8devym">
              {allActivities.slice(0, 4).map((activity, activityIdx) => <li key={activityIdx} data-id="element-4011" data-mp="dpo069sneyc3" data-mpi={activityIdx}>
                  <div className="relative pb-8" data-id="element-4012" data-mp="3m6sn6w7t7vny">
                    {activityIdx !== 3 && <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-slate-100" aria-hidden="true" data-id="element-4013" data-mp="3i5fjaroa22jo" />}
                    <div className="relative flex space-x-3" data-id="element-4014" data-mp="3o3tv9x3faiw">
                      <div data-id="element-4015" data-mp="3manlvqe6sbf">
                        <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-4 ring-white ${activity.bg}`} data-id="element-4016" data-mp="8nq9fq0e92uv">
                          <activity.icon className={`h-4 w-4 ${activity.text}`} aria-hidden="true" data-id="element-4017" data-mp="dfzx0ze1dqtz" />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4" data-id="element-4018" data-mp="rdoh450a4txz">
                        <div data-id="element-4019" data-mp="wdatydc74869">
                          <p className="text-sm text-slate-500" data-id="element-4020" data-mp="wcix6z6v44ee">
                            <span className="font-semibold text-seform-navy" data-id="element-4021" data-mp="2t649es0d2m57">
                              {activity.user}
                            </span>{' '}
                            {activity.action}{' '}
                            <span className="font-semibold text-seform-navy" data-id="element-4022" data-mp="2qly3wfw3mnmj">
                              {activity.target}
                            </span>
                          </p>
                        </div>
                        <div className="text-right text-xs whitespace-nowrap text-slate-400" data-id="element-4023" data-mp="2gvl9lvyhfg37">
                          {activity.date}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>)}
            </ul>
          </div>
        </Card>

        {/* Upcoming Deadlines */}
        <Card title="Scadenze imminenti" description="Attività da completare nei prossimi 7 giorni" actions={<Button variant="light" size="sm" onClick={() => navigate('/deadlines')} data-id="element-4025" data-mp="2ulyn0du57mzx">
              Vedi calendario
            </Button>} data-id="element-4024" data-mp="2zl1o5n70592m">
          <div className="space-y-3" data-id="element-4026" data-mp="1j9fl8qmed5pk">
            {deadlinesData.map(item => <div key={item.id} onClick={() => handleDeadlineClick(item)} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 hover:bg-white hover:shadow-sm transition-all cursor-pointer group" data-id="element-4027" data-mp="b8duv0kf7grd">
                <div className="flex items-center gap-3" data-id="element-4028" data-mp="66ya79m5nc9i">
                  <div className={`w-2 h-2 rounded-full ${item.priority === 'high' ? 'bg-red-500' : item.priority === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'}`} data-id="element-4029" data-mp="3np0za8s7271b"></div>
                  <div data-id="element-4030" data-mp="3epnpff05rgg2">
                    <h4 className="text-sm font-semibold text-seform-navy group-hover:text-seform-orange transition-colors" data-id="element-4031" data-mp="3eq5qqq1ogkuu">
                      {item.title}
                    </h4>
                    <p className="text-xs text-slate-500" data-id="element-4032" data-mp="34rq00o6kbdlv">{item.client}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-4033" data-mp="39oq6h07bqidi">
                  <span className="text-xs font-medium text-slate-500 bg-white px-2 py-1 rounded-md border border-slate-100" data-id="element-4034" data-mp="2q2y5fnwawh3w">
                    {item.date}
                  </span>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-4035" data-mp="301m6pb02ghkw">
                    <button onClick={e => handleDeadlineEdit(item, e)} className="p-1.5 rounded-md hover:bg-slate-100 transition-colors" title="Modifica scadenza" data-id="element-4036" data-mp="2v29o52g89wo4">
                      <Edit className="h-4 w-4 text-slate-400 hover:text-slate-600" data-id="element-4037" data-mp="350qj0aiaevfg" />
                    </button>
                    <button onClick={e => handleDeadlineDelete(item, e)} className="p-1.5 rounded-md hover:bg-slate-100 transition-colors" title="Elimina scadenza" data-id="element-4038" data-mp="3iu81drz8m23w">
                      <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-500" data-id="element-4039" data-mp="3nsjag0hpk8l2" />
                    </button>
                  </div>
                </div>
              </div>)}
          </div>
        </Card>
      </div>
    </>;
  const renderCommerciale = () => <>
      {/* Period Filter */}
      <div className="flex flex-wrap gap-3 items-end mb-8" data-id="element-4040" data-mp="2idv33getnfgv">
        <div className="w-40" data-id="element-4041" data-mp="2ncy9p5ag9tqy">
          <Select options={[{
          value: 'month',
          label: 'Mese corrente'
        }, {
          value: 'year',
          label: 'Anno corrente'
        }, {
          value: 'custom',
          label: 'Personalizzato'
        }]} value={period} onChange={e => setPeriod(e.target.value)} data-id="element-4042" data-mp="2nb3p5s33c3ne" />
        </div>
        {period === 'custom' && <div className="flex gap-2" data-id="element-4043" data-mp="16xwo6vwypz7q">
            <DatePicker value={customStart} onChange={e => setCustomStart(e.target.value)} data-id="element-4044" data-mp="1bwzvyo4bujje" />
            <DatePicker value={customEnd} onChange={e => setCustomEnd(e.target.value)} data-id="element-4045" data-mp="3p6813j3dzzy0" />
          </div>}
        <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-4047" data-mp="3g8nyo6tsfxlt" />} data-id="element-4046" data-mp="3g6uowinvox9l">
          Esporta report
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-4048" data-mp="3696uxuy3jtsh">
        <StatCard title="Obiettivo offerte positive" value="€ 12.500" change="+25% vs Target" trend="up" icon={Target} color="bg-green-500" isStatic data-id="element-4049" data-mp="31hyl4xsuu6es" />
        <StatCard title="Totale offerte" value="€ 45.200" change="15 Offerte" trend="neutral" icon={FileText} color="bg-blue-500" isStatic data-id="element-4050" data-mp="2si1s6jzfmldx" />
        <StatCard title="Tasso conversione" value="65%" change="+5% vs Mese sc." trend="up" icon={TrendingUp} color="bg-seform-orange" isStatic data-id="element-4051" data-mp="2ikdtjiypgtqh" />
        <StatCard title="Media mensile positivi" value="€ 13.800" change="6 mesi" trend="neutral" icon={PieChart} color="bg-yellow-500" isStatic data-id="element-4052" data-mp="2c9k1aieuihdx" />
      </div>

      {/* Charts */}
      <div className="space-y-8" data-id="element-4053" data-mp="1ycx3yeizdp7h">
        <Card title="Stato offerte" noHoverShadow data-id="element-4054" data-mp="1yd6jt9abafzg">
          <div className="h-[280px] w-full relative" data-id="element-4055" data-mp="1tkn9cl6x80yv">
            <ResponsiveContainer width="100%" height="100%" data-id="element-4056" data-mp="1tj2cuq11q7yd">
              <RechartsPieChart data-id="element-4057" data-mp="1on3pn5rgevzj">
                <Pie data={conversionData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" data-id="element-4058" data-mp="1ojzh5w6yhe32">
                  {conversionData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} style={{
                  outline: 'none'
                }} data-id="element-4059" data-mp="3m9wgncas8854" data-mpi={index} />)}
                </Pie>
                <Pie data={conversionTargetData} cx="50%" cy="50%" innerRadius={86} outerRadius={94} paddingAngle={5} dataKey="value" legendType="none" data-id="element-4060" data-mp="441aw2cd387k">
                  {conversionTargetData.map((entry, index) => <Cell key={`target-cell-${index}`} fill={COLORS[index % COLORS.length]} fillOpacity={TARGET_OPACITY} style={{
                  outline: 'none'
                }} data-id="element-4061" data-mp="3chqjzp9l52jl" data-mpi={index} />)}
                </Pie>
                <Tooltip wrapperStyle={{
                zIndex: 100
              }} data-id="element-4062" data-mp="3s4cxwv8wvmvt" />
                <Legend verticalAlign="bottom" height={50} wrapperStyle={{
                paddingTop: '20px'
              }} data-id="element-4063" data-mp="3n5jszr7ox479" />
              </RechartsPieChart>
            </ResponsiveContainer>
            <div className="absolute top-[50%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none flex flex-col items-center justify-center" data-id="element-4064" data-mp="397brr73mfgqa">
              <p className="text-3xl font-bold text-seform-navy leading-none" data-id="element-4065" data-mp="3i7i0kfodjsj4">
                45
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-4066" data-mp="3d7m5j5xnnbfp">Totali</p>
            </div>
          </div>
          <p className="text-xs text-slate-500 text-center" data-id="element-4067" data-mp="xfya5eyahqz0">
            Anello esterno tenue: obiettivo (Positivi 34 · Negativi 4 · In valutazione 7)
          </p>
          <div className="mt-4 space-y-3" data-id="element-4068" data-mp="10yotnjogqtoi">
            <div className="flex justify-between items-center p-2 bg-green-50 rounded-lg" data-id="element-4069" data-mp="10xm5vkmft7ct">
              <span className="text-sm font-medium text-green-700" data-id="element-4070" data-mp="151bntr8lwqov">
                Positivi
              </span>
              <span className="font-bold text-green-700" data-id="element-4071" data-mp="r1sqlthc2zrc">
                € 24.500
                <span className="ml-2 text-xs font-medium text-green-700/60" data-id="element-4072" data-mp="w0nakyr1eud1">
                  obiettivo € 30.000
                </span>
              </span>
            </div>
            <div className="flex justify-between items-center p-2 bg-yellow-50 rounded-lg relative z-10" data-id="element-4073" data-mp="1onzu101wim24">
              <span className="text-sm font-medium text-yellow-700" data-id="element-4074" data-mp="19zvcw0ihyilg">
                In valutazione
              </span>
              <span className="font-bold text-yellow-700" data-id="element-4075" data-mp="1exesb3qxl1ez">€ 12.200</span>
            </div>
            <div className="flex justify-between items-center p-2 bg-red-50 rounded-lg relative z-10" data-id="element-4076" data-mp="22nr8t308s6h6">
              <span className="text-sm font-medium text-red-700" data-id="element-4077" data-mp="27fsoz00j6hch">Negativi</span>
              <span className="font-bold text-red-700" data-id="element-4078" data-mp="itfzf5b3nifg">€ 8.500</span>
            </div>
          </div>
        </Card>

        <div className="h-px bg-slate-200" data-id="element-4079" data-mp="eq9uc0zq3xak"></div>

        <Card title="Trend offerte vs positive" className="min-h-[400px]" noHoverShadow data-id="element-4080" data-mp="etpcxdslvogq">
          <div className="h-[300px] w-full mt-6" data-id="element-4081" data-mp="4tooxejskk67">
            <ResponsiveContainer width="100%" height="100%" data-id="element-4082" data-mp="4uzqr8tofhuo">
              <LineChart data={trendData} data-id="element-4083" data-mp="12h6zmux0jwcu">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-4084" data-mp="12j03y3k5m4ho" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fill: '#64748B'
              }} dy={20} data-id="element-4085" data-mp="sq2ibojv7zeu" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#64748B'
              }} data-id="element-4086" data-mp="1l980g2tclwow" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-4087" data-mp="1nu7ixxn5xgy5" />
                <Legend wrapperStyle={{
                paddingTop: '40px'
              }} data-id="element-4088" data-mp="22pdm8cuvj1ns" />
                <Line type="monotone" dataKey="offerte" stroke="#94A3B8" strokeWidth={2} dot={false} name="Totale offerte" data-id="element-4089" data-mp="26qff8by76hhb" />
                <Line type="monotone" dataKey="positivi" stroke="#FF9500" strokeWidth={2} activeDot={{
                r: 8
              }} name="Positivi" data-id="element-4090" data-mp="2lg4s34fnk9xf" />
                <Line type="monotone" dataKey="targetOfferte" stroke="#94A3B8" strokeOpacity={TARGET_OPACITY + 0.15} strokeWidth={2} strokeDasharray="6 4" dot={false} name="Obiettivo offerte" data-id="element-4091" data-mp="vb9xdd2wmq57" />
                <Line type="monotone" dataKey="targetPositivi" stroke="#FF9500" strokeOpacity={TARGET_OPACITY + 0.15} strokeWidth={2} strokeDasharray="6 4" dot={false} name="Obiettivo positivi" data-id="element-4092" data-mp="zbtuwhre3vrv" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <div className="h-px bg-slate-200" data-id="element-4093" data-mp="3otst1rypdjdv"></div>

        <Card title="Performance per commerciale" className="min-h-[400px]" noHoverShadow data-id="element-4094" data-mp="3oukon2savh45">
          <div className="h-[300px] w-full mt-6" data-id="element-4095" data-mp="3aoisee32v3le">
            <ResponsiveContainer width="100%" height="100%" data-id="element-4096" data-mp="3aptwkf0fqdd1">
              <RechartsBarChart data={salesRepData} layout="vertical" data-id="element-4097" data-mp="3epvy8o6676sb">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" data-id="element-4098" data-mp="3es8hr5rktp7h" />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{
                fill: '#64748B'
              }} data-id="element-4099" data-mp="35wiyb29ov4wq" />
                <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{
                fill: '#64748B'
              }} width={100} data-id="element-4100" data-mp="2w0xncemn6uau" />
                <Tooltip cursor={{
                fill: '#F1F5F9'
              }} data-id="element-4101" data-mp="g99uovenvnxu" />
                <Legend wrapperStyle={{
                paddingTop: '20px'
              }} data-id="element-4102" data-mp="1ts0qdc7skj96" />
                <Bar dataKey="positivi" fill="#FF9500" radius={[0, 4, 4, 0]} name="Positivi (€)" barSize={20} data-id="element-4103" data-mp="1osnvc46ro5vi" />
                <Bar dataKey="target" fill="#FF9500" fillOpacity={TARGET_OPACITY} radius={[0, 4, 4, 0]} name="Target (€)" barSize={20} data-id="element-4104" data-mp="1fzu7hid97y61" />
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </>;
  const renderEconomica = () => <>
      {/* Period Filter */}
      <div className="flex flex-wrap gap-3 items-end mb-8" data-id="element-4105" data-mp="2vhdvdd4j18k3">
        <div className="w-40" data-id="element-4106" data-mp="2vguhq8lwt6bv">
          <Select options={[{
          value: 'month',
          label: 'Mese corrente'
        }, {
          value: 'year',
          label: 'Anno corrente'
        }, {
          value: 'custom',
          label: 'Personalizzato'
        }]} value={period} onChange={e => setPeriod(e.target.value)} data-id="element-4107" data-mp="2rfspwcubnwjx" />
        </div>
        {period === 'custom' && <div className="flex gap-2" data-id="element-4108" data-mp="3qetauiqwvt5d">
            <DatePicker value={customStart} onChange={e => setCustomStart(e.target.value)} data-id="element-4109" data-mp="3qfusj62fndw2" />
            <DatePicker value={customEnd} onChange={e => setCustomEnd(e.target.value)} data-id="element-4110" data-mp="3vfh83m5qh681" />
          </div>}
        <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-4112" data-mp="1eps82uw1jxmx" />} data-id="element-4111" data-mp="89pz4f6fwivz">
          Esporta report
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-4113" data-mp="1jmio5i7uit8c">
        <StatCard title="Fatturato emesso" value="€ 59.000" change="+8% vs Mese sc." trend="up" icon={Euro} color="bg-seform-navy" isStatic data-id="element-4114" data-mp="2ar7q0ntbunks" />
        <StatCard title="Proformato" value="€ 12.000" change="5 Proforme" trend="neutral" icon={Receipt} color="bg-blue-500" isStatic data-id="element-4115" data-mp="20ztl20ilwvk9" />
        <StatCard title="Totale produzione" value="€ 71.000" change="118% del Target" trend="up" icon={TrendingUp} color="bg-green-500" isStatic data-id="element-4116" data-mp="1s1hyc9kv2hx3" />
        <StatCard title="Da incassare" value="€ 24.500" change="3 Scadute" trend="down" icon={AlertCircle} color="bg-red-500" isStatic data-id="element-4117" data-mp="1n02ayav7nzbj" />
      </div>

      {/* Main Chart */}
      <Card title="Andamento fatturato vs target" className="mb-8 min-h-[450px]" noHoverShadow data-id="element-4118" data-mp="3hdtjt85puf5x">
        <div className="h-[350px] w-full mt-6" data-id="element-4119" data-mp="15on9z90d7l7t">
          <ResponsiveContainer width="100%" height="100%" data-id="element-4120" data-mp="10sy6wvp4zm6r">
            <AreaChart data={revenueData} data-id="element-4121" data-mp="1fgt2cjuj44nj">
              <defs data-id="element-4122" data-mp="1fhkypwvya6ev">
                <linearGradient id="colorFatturato" x1="0" y1="0" x2="0" y2="1" data-id="element-4123" data-mp="1fi46ikrvzef6">
                  <stop offset="5%" stopColor="#2D1B69" stopOpacity={0.8} data-id="element-4124" data-mp="1apb25y0by83m" />
                  <stop offset="95%" stopColor="#2D1B69" stopOpacity={0} data-id="element-4125" data-mp="1ogogmxjdsfrs" />
                </linearGradient>
                <linearGradient id="colorProformato" x1="0" y1="0" x2="0" y2="1" data-id="element-4126" data-mp="1jhcypl7a348m">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} data-id="element-4127" data-mp="1tgau9834fy14" />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} data-id="element-4128" data-mp="41crvj9hk8xe" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-4129" data-mp="3v8p7htuq0eb7" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
              fill: '#64748B'
            }} dy={20} data-id="element-4130" data-mp="osrpdnw9ejn4" />
              <YAxis axisLine={false} tickLine={false} tick={{
              fill: '#64748B'
            }} data-id="element-4131" data-mp="12qzljtlh1gx8" />
              <Tooltip contentStyle={{
              borderRadius: '8px',
              border: 'none',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
            }} data-id="element-4132" data-mp="1br4rdzb0o79a" />
              <Legend wrapperStyle={{
              paddingTop: '40px'
            }} data-id="element-4133" data-mp="1lh7ks0m1vn85" />
              <Area type="monotone" dataKey="fatturato" stroke="#2D1B69" fillOpacity={1} fill="url(#colorFatturato)" name="Fatturato (€)" data-id="element-4134" data-mp="1qhlsfhjqov5n" />
              <Area type="monotone" dataKey="proformato" stroke="#3B82F6" fillOpacity={1} fill="url(#colorProformato)" name="Proformato (€)" data-id="element-4135" data-mp="3aycslbs71w1v" />
              <Area type="monotone" dataKey="target" stroke="#2D1B69" strokeOpacity={TARGET_OPACITY + 0.15} strokeWidth={2} strokeDasharray="5 5" fill="none" name="Target (€)" data-id="element-4136" data-mp="311gm1bluvjzy" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Breakdown Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-4137" data-mp="3jwx49szc75gl">
        <Card title="Analisi incassi" noHoverShadow data-id="element-4138" data-mp="lac7ey0emhvp">
          <div className="space-y-6" data-id="element-4139" data-mp="giaxhiqpl38u">
            <div data-id="element-4140" data-mp="gh9calfzb0de">
              <div className="flex justify-between mb-1" data-id="element-4141" data-mp="ggq43wg4rqck">
                <span className="text-sm font-medium text-slate-700" data-id="element-4142" data-mp="zrr0t5t2v5k">
                  Incassato (70%)
                </span>
                <span className="text-sm font-medium text-slate-700" data-id="element-4143" data-mp="60p8f3izrl60">
                  € 41.300
                </span>
              </div>
              <TargetProgressBar actual={70} target={collectionTargets.incassato} max={100} colorClass="bg-green-600" label={`Incassato 70%, obiettivo ${collectionTargets.incassato}%`} data-id="element-4144" data-mp="3s6cgw9k4jx10" />
              <p className="text-xs text-slate-400 mt-1.5" data-id="element-4145" data-mp="oroqdks2jups">
                Obiettivo {collectionTargets.incassato}%
              </p>
            </div>
            <div data-id="element-4146" data-mp="eyr5x93invoj">
              <div className="flex justify-between mb-1" data-id="element-4147" data-mp="ey7zaojbgtpt">
                <span className="text-sm font-medium text-slate-700" data-id="element-4148" data-mp="12ov95fcsltf0">
                  In scadenza (20%)
                </span>
                <span className="text-sm font-medium text-slate-700" data-id="element-4149" data-mp="17gvfpqndbkkk">
                  € 11.800
                </span>
              </div>
              <TargetProgressBar actual={20} target={collectionTargets.inScadenza} max={100} colorClass="bg-yellow-400" label={`In scadenza 20%, obiettivo massimo ${collectionTargets.inScadenza}%`} data-id="element-4150" data-mp="2s45xzhr86k7a" />
              <p className="text-xs text-slate-400 mt-1.5" data-id="element-4151" data-mp="2wzbt976ipb81">
                Obiettivo max {collectionTargets.inScadenza}%
              </p>
            </div>
            <div data-id="element-4152" data-mp="249lf4y4vy9bx">
              <div className="flex justify-between mb-1" data-id="element-4153" data-mp="24a4k7e9ft38j">
                <span className="text-sm font-medium text-slate-700" data-id="element-4154" data-mp="2i8ui2w42z6xy">
                  Scaduto (10%)
                </span>
                <span className="text-sm font-medium text-slate-700" data-id="element-4155" data-mp="2e5oi267kabzx">
                  € 5.900
                </span>
              </div>
              <TargetProgressBar actual={10} target={collectionTargets.scaduto} max={100} colorClass="bg-red-500" label={`Scaduto 10%, obiettivo massimo ${collectionTargets.scaduto}%`} data-id="element-4156" data-mp="3tpkq8ks2e8u7" />
              <p className="text-xs text-slate-400 mt-1.5" data-id="element-4157" data-mp="2r88u3atmj8xc">
                Obiettivo max {collectionTargets.scaduto}%
              </p>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-slate-100 flex justify-end" data-id="element-4158" data-mp="309qjjjsx8myp">
            <Button variant="light" color="primary" onClick={() => navigate('/invoicing')} data-id="element-4159" data-mp="23l119sddtsc7">
              Vedi scadenziario completo
            </Button>
          </div>
        </Card>

        <Card title="Top clienti (fatturato da inizio anno)" noHoverShadow actions={<Button variant="light" size="sm" onClick={() => setShowTopClientsModal(true)} data-id="element-4161" data-mp="2he0f7pxinehn">
              Vedi tutti
            </Button>} data-id="element-4160" data-mp="2cjmhhgvdsv9t">
          <div className="overflow-x-auto" data-id="element-4162" data-mp="1ot8mbhhi2gb0">
            <table className="w-full text-left" data-id="element-4163" data-mp="1hb0zaovgmqiz">
              <thead data-id="element-4164" data-mp="1h9pxuvphluv1">
                <tr className="text-xs text-slate-500 uppercase border-b border-slate-100" data-id="element-4165" data-mp="1cd8yt6gr1w36">
                  <th className="pb-3 font-semibold" data-id="element-4166" data-mp="18b5ju7uvg9d5">Cliente</th>
                  <th className="pb-3 font-semibold text-right" data-id="element-4167" data-mp="189kr8ns4b0hx">Fatturato</th>
                  <th className="pb-3 font-semibold text-right" data-id="element-4168" data-mp="13bj6zziygv4v">% Totale</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50" data-id="element-4169" data-mp="yej1pqtxjwet">
                {topClientsData.slice(0, 5).map((client, i) => <tr key={i} data-id="element-4170" data-mp="onelknsiq5ie" data-mpi={i}>
                    <td className="py-3 text-sm font-medium text-seform-navy" data-id="element-4171" data-mp="omn38enxm4a2">
                      {client.name}
                    </td>
                    <td className="py-3 text-sm text-right text-slate-600" data-id="element-4172" data-mp="knnyau27a7im">
                      {client.amount}
                    </td>
                    <td className="py-3 text-sm text-right text-slate-500" data-id="element-4173" data-mp="anx5qxx7a2l6">
                      {client.perc}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </>;
  const renderObiettivi = () => <>
      {/* Period Filter */}
      <div className="flex flex-wrap gap-3 items-end mb-8" data-id="element-4174" data-mp="1pive96ymzj1b">
        <div className="w-40" data-id="element-4175" data-mp="1uihttn1xtbda">
          <Select options={[{
          value: 'month',
          label: 'Mese corrente'
        }, {
          value: 'year',
          label: 'Anno corrente'
        }, {
          value: 'custom',
          label: 'Personalizzato'
        }]} value={period} onChange={e => setPeriod(e.target.value)} data-id="element-4176" data-mp="1uhf1daoyv0v9" />
        </div>
        {period === 'custom' && <div className="flex gap-2" data-id="element-4177" data-mp="wvhlll1z19n0">
            <DatePicker value={customStart} onChange={e => setCustomStart(e.target.value)} data-id="element-4178" data-mp="11u2mlmp1rxci" />
            <DatePicker value={customEnd} onChange={e => setCustomEnd(e.target.value)} data-id="element-4179" data-mp="i9bvucn7uejk" />
          </div>}
        <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-4181" data-mp="s4dnbf4yrspb" />} data-id="element-4180" data-mp="s6gmaoo3gw66">
          Esporta
        </Button>
        <Button onClick={() => setShowObjectivesModal(true)} startContent={<Plus className="h-4 w-4" data-id="element-4183" data-mp="115k65uv35flq" />} data-id="element-4182" data-mp="n62e96mhtm85">
          Imposta obiettivi
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-4184" data-mp="w67ix90ay6im">
        <Card title="Preventivi positivi da inizio anno" noHoverShadow data-id="element-4185" data-mp="1awev56zputav">
          <div className="flex items-end justify-between" data-id="element-4186" data-mp="15xlhnekfj2ap">
            <div className="min-w-0 flex-1" data-id="element-4187" data-mp="1607o1avl3brc">
              <p className="text-3xl font-bold text-seform-navy truncate" data-id="element-4188" data-mp="1jyo2jnzou6k1">
                € 82.800
              </p>
              <p className="text-sm text-slate-500 mt-1" data-id="element-4189" data-mp="1fxm3p89onwil">Target: € 74.000</p>
            </div>
            <div className="text-right flex-shrink-0 ml-4" data-id="element-4190" data-mp="1nm3ewt1t7rl1">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-4191" data-mp="1in0k01fjn1qs">
                +11.9%
              </span>
            </div>
          </div>
          <div className="mt-4" data-id="element-4192" data-mp="1skenz1ka5y27">
            <TargetProgressBar actual={82800} target={74000} colorClass="bg-seform-orange" height="sm" label="Reale € 82.800, target € 74.000" data-id="element-4193" data-mp="1sj3o3ctyhagd" />
          </div>
        </Card>
        <Card title="Fatturato da inizio anno" noHoverShadow data-id="element-4194" data-mp="2gashyxmfe6zf">
          <div className="flex items-end justify-between" data-id="element-4195" data-mp="2g869mvrpnng5">
            <div className="min-w-0 flex-1" data-id="element-4196" data-mp="2bc7ferjpbb7t">
              <p className="text-3xl font-bold text-seform-navy truncate" data-id="element-4197" data-mp="2b9bm5gistapp">
                € 272.200
              </p>
              <p className="text-sm text-slate-500 mt-1" data-id="element-4198" data-mp="gxeoh5trj8m7">Target: € 270.000</p>
            </div>
            <div className="text-right flex-shrink-0 ml-4" data-id="element-4199" data-mp="2skqksbq69cq9">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-4200" data-mp="2sla1jp435v2p">
                +0.8%
              </span>
            </div>
          </div>
          <div className="mt-4" data-id="element-4201" data-mp="2iskur7jhd5dp">
            <TargetProgressBar actual={272200} target={270000} colorClass="bg-seform-navy" height="sm" label="Reale € 272.200, target € 270.000" data-id="element-4202" data-mp="2nti733ht757a" />
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" data-id="element-4203" data-mp="1zw2t9yetxr49">
        <Card title="Preventivi: target vs reale" noHoverShadow data-id="element-4204" data-mp="1zxnqxwwfxq6c">
          <div className="h-[300px] w-full mt-6" data-id="element-4205" data-mp="24vyx9xnj1ijt">
            <ResponsiveContainer width="100%" height="100%" data-id="element-4206" data-mp="24wqrp55e1a8i">
              <ComposedChart data={objectivesData} data-id="element-4207" data-mp="3uadiw9390pb3">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-4208" data-mp="33aq5d7riayr" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fill: '#64748B'
              }} dy={10} data-id="element-4209" data-mp="1nowu4cfnhp9" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#64748B'
              }} data-id="element-4210" data-mp="3ivahkc57362g" />
                <Tooltip data-id="element-4211" data-mp="fd8uivr5porp" />
                <Legend wrapperStyle={{
                paddingTop: '20px'
              }} data-id="element-4212" data-mp="ff3cc174t0rk" />
                <Bar dataKey="actualSales" fill="#FF9500" name="Reale" barSize={20} radius={[4, 4, 0, 0]} data-id="element-4213" data-mp="akpe7r134fj7" />
                <Line type="monotone" dataKey="targetSales" stroke="#FF9500" strokeOpacity={TARGET_OPACITY + 0.15} strokeDasharray="6 4" strokeWidth={2} dot={false} name="Target" data-id="element-4214" data-mp="ycmnbad5gtgm" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Fatturato: target vs reale" noHoverShadow data-id="element-4215" data-mp="1km38j0h5rcxe">
          <div className="h-[300px] w-full mt-6" data-id="element-4216" data-mp="1pk4qtj6rf87t">
            <ResponsiveContainer width="100%" height="100%" data-id="element-4217" data-mp="1pjd3evnbggsm">
              <ComposedChart data={objectivesData} data-id="element-4218" data-mp="1tl85w3gzh07z">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-4219" data-mp="1tjx5mdmqyalm" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fill: '#64748B'
              }} dy={10} data-id="element-4220" data-mp="23bt2xmh0dwos" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#64748B'
              }} data-id="element-4221" data-mp="2d7cvbywd6d8s" />
                <Tooltip data-id="element-4222" data-mp="34zwxq2s4p74c" />
                <Legend wrapperStyle={{
                paddingTop: '20px'
              }} data-id="element-4223" data-mp="3jqnrvhh70iam" />
                <Bar dataKey="actualRevenue" fill="#2D1B69" name="Reale" barSize={20} radius={[4, 4, 0, 0]} data-id="element-4224" data-mp="3et5ffh3yvkxv" />
                <Line type="monotone" dataKey="targetRevenue" stroke="#2D1B69" strokeOpacity={TARGET_OPACITY + 0.15} strokeDasharray="6 4" strokeWidth={2} dot={false} name="Target" data-id="element-4225" data-mp="2m4zv2vgtrxyk" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Data Table */}
      <Card noPadding className="overflow-hidden" noHoverShadow data-id="element-4226" data-mp="itlfxaun3iz3">
        <div className="p-4 flex justify-end border-b border-slate-100" data-id="element-4227" data-mp="ntg77fibuako">
          <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="objectives" lockedColumns={['month']} data-id="element-4228" data-mp="nrmzrxzw3mbm" />
        </div>
        <div className="overflow-x-auto" data-id="element-4229" data-mp="11kuqzok26fss">
          <table className="w-full text-left border-collapse" data-id="element-4230" data-mp="11n7iotfmc4j1">
            <thead data-id="element-4231" data-mp="16j6180e9msbj">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-4232" data-mp="16jo2jbfsbwqb">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-6 py-4 whitespace-pre-line align-top" data-id="element-4233" data-mp="1kjqn55fh9499">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-4234" data-mp="1u7kydpszt0o5">
              {objectivesData.map(item => {
              const diffSales = item.actualSales - item.targetSales;
              const diffRevenue = item.actualRevenue - item.targetRevenue;
              return <tr key={item.id} className="hover:bg-slate-50/80 transition-colors" data-id="element-4235" data-mp="1l991j61j96n9">
                    {visibleColumns.includes('month') && <td className="px-6 py-4 font-medium text-slate-900" data-id="element-4236" data-mp="16i04ibvawj03">
                        {item.month}
                      </td>}
                    {visibleColumns.includes('targetSales') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-4237" data-mp="xhvbj6qutn5g">
                        {formatCurrency(item.targetSales)}
                      </td>}
                    {visibleColumns.includes('actualSales') && <td className="px-6 py-4 text-sm font-bold text-seform-navy" data-id="element-4238" data-mp="345cgk7zksznx">
                        {formatCurrency(item.actualSales)}
                      </td>}
                    {visibleColumns.includes('diffSales') && <td className={`px-6 py-4 text-sm font-medium ${diffSales >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-4239" data-mp="3i5n5v6l8m66z">
                        {diffSales >= 0 ? '+' : ''}
                        {formatCurrency(diffSales)}
                      </td>}
                    {visibleColumns.includes('targetRevenue') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-4240" data-mp="2z716bw5dcn56">
                        {formatCurrency(item.targetRevenue)}
                      </td>}
                    {visibleColumns.includes('actualRevenue') && <td className="px-6 py-4 text-sm font-bold text-seform-navy" data-id="element-4241" data-mp="26odkufmgn63k">
                        {formatCurrency(item.actualRevenue)}
                      </td>}
                    {visibleColumns.includes('diffRevenue') && <td className={`px-6 py-4 text-sm font-medium ${diffRevenue >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-4242" data-mp="1a1butz80fet1">
                        {diffRevenue >= 0 ? '+' : ''}
                        {formatCurrency(diffRevenue)}
                      </td>}
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>
    </>;
  return <Layout data-id="element-4243" data-mp="1nsgn2g91t4tc">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4" data-id="element-4244" data-mp="1nti0grdje3e8">
        <div data-id="element-4245" data-mp="21t9lvd6dum29">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-4246" data-mp="21srgnr1qsxi7">Dashboard</h1>
          <p className="text-slate-500 mt-1" data-id="element-4247" data-mp="1xrfr1livcca0">Benvenuto, {userName}.</p>
        </div>
        <div className="flex gap-2" data-id="element-4248" data-mp="2gef4pnb2gm6c">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-sm font-medium" data-id="element-4249" data-mp="2gfgg5svzvaol">
            <Calendar className="h-4 w-4" data-id="element-4250" data-mp="2lbnofsbv4y9p" />
            Oggi: 12 maggio
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-8" data-id="element-4251" data-mp="2cck28fheg2qz">
        <div className="border-b border-slate-200" data-id="element-4252" data-mp="2cbs7f6vmm91r">
          <nav className="-mb-px flex space-x-8" data-id="element-4253" data-mp="1xnxd5m1yzwmk">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id ? 'border-seform-orange text-seform-orange' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-700'}`} data-id="element-4254" data-mp="22f5nckb4jm0s">
                {tab.label}
              </button>)}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="transition-opacity duration-200" data-id="element-4255" data-mp="1jqimkdzzdit0">
        {activeTab === 'operativa' && renderOperativa()}
        {activeTab === 'commerciale' && renderCommerciale()}
        {activeTab === 'economica' && renderEconomica()}
        {activeTab === 'obiettivi' && renderObiettivi()}
      </div>

      {/* Activities Modal */}
      <Modal isOpen={showActivitiesModal} onClose={() => setShowActivitiesModal(false)} title="Tutte le attività recenti" size="lg" data-id="element-4256" data-mp="1v0s0zulc967h">
        <div className="space-y-4 max-h-[60vh] overflow-y-auto" data-id="element-4257" data-mp="24yplq83zokvc">
          {allActivities.map((activity, idx) => <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg" data-id="element-4258" data-mp="29owaq92ey7e4" data-mpi={idx}>
              <span className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${activity.bg}`} data-id="element-4259" data-mp="2dppzt5rlds8l">
                <activity.icon className={`h-4 w-4 ${activity.text}`} data-id="element-4260" data-mp="2np74bjv3y02z" />
              </span>
              <div className="flex-1 min-w-0" data-id="element-4261" data-mp="2xt48vfdfzr1q">
                <p className="text-sm text-slate-600" data-id="element-4262" data-mp="2xtx7ft28za8s">
                  <span className="font-semibold text-seform-navy" data-id="element-4263" data-mp="2su13tuxgpktr">
                    {activity.user}
                  </span>{' '}
                  {activity.action}{' '}
                  <span className="font-semibold text-seform-navy" data-id="element-4264" data-mp="2nx0wlgovmc12">
                    {activity.target}
                  </span>
                </p>
                <p className="text-xs text-slate-400 mt-1" data-id="element-4265" data-mp="3gjvfwa9yj5cy">{activity.date}</p>
              </div>
            </div>)}
        </div>
      </Modal>

      {/* Top Clients Modal */}
      <Modal isOpen={showTopClientsModal} onClose={() => setShowTopClientsModal(false)} title="Top clienti (fatturato da inizio anno)" size="lg" data-id="element-4266" data-mp="36taiur9xm8w5">
        <div className="overflow-x-auto max-h-[60vh]" data-id="element-4267" data-mp="34pc59kuekss">
          <table className="w-full text-left" data-id="element-4268" data-mp="36a54uridvoj">
            <thead className="sticky top-0 bg-white" data-id="element-4269" data-mp="3ujn04sr2hhze">
              <tr className="text-xs text-slate-500 uppercase border-b border-slate-100" data-id="element-4270" data-mp="3uhtoez1lk7kj">
                <th className="pb-3 font-semibold" data-id="element-4271" data-mp="1smcsoguu50bb">#</th>
                <th className="pb-3 font-semibold" data-id="element-4272" data-mp="1xknzeipu2upb">Cliente</th>
                <th className="pb-3 font-semibold text-right" data-id="element-4273" data-mp="1xn0g6sjuuz0s">Fatturato</th>
                <th className="pb-3 font-semibold text-right" data-id="element-4274" data-mp="1ivi5995uruoa">% Totale</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50" data-id="element-4275" data-mp="1ntbb2138umo1">
              {topClientsData.map((client, i) => <tr key={i} className="hover:bg-slate-50" data-id="element-4276" data-mp="19xfz6f79ibu5" data-mpi={i}>
                  <td className="py-3 text-sm text-slate-400" data-id="element-4277" data-mp="1evhd6lptzkyr">{i + 1}</td>
                  <td className="py-3 text-sm font-medium text-seform-navy" data-id="element-4278" data-mp="zxp1y5rbhiqd">
                    {client.name}
                  </td>
                  <td className="py-3 text-sm text-right text-slate-600" data-id="element-4279" data-mp="14x1o0oad6lrw">
                    {client.amount}
                  </td>
                  <td className="py-3 text-sm text-right text-slate-500" data-id="element-4280" data-mp="2u9dk0gsui1ez">
                    {client.perc}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Modal>

      {/* Objectives Modal */}
      <Modal isOpen={showObjectivesModal} onClose={() => setShowObjectivesModal(false)} title="Imposta obiettivi" size="lg" data-id="element-4281" data-mp="ip5yws8bjtb3">
        <div className="space-y-6" data-id="element-4282" data-mp="smalgt62tqsf">
          {/* Monthly / Annual Toggle */}
          <div className="bg-slate-100 p-1 rounded-xl flex items-center w-fit mx-auto" data-id="element-4283" data-mp="sk7rjy1szdif">
            <button onClick={() => setObjectivePeriodType('monthly')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${objectivePeriodType === 'monthly' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4284" data-mp="16fidgly2koan">
              Obiettivi mensili
            </button>
            <button onClick={() => setObjectivePeriodType('annual')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${objectivePeriodType === 'annual' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4285" data-mp="3i3i1nvcptf6m">
              Obiettivi annuali
            </button>
          </div>

          <section data-id="element-4286" data-mp="3okjl3ap2x55d">
            <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4287" data-mp="3ok05w1qtcuv1">
              <Target className="h-4 w-4" data-id="element-4288" data-mp="3tfyu9pc56cvg" />{' '}
              {objectivePeriodType === 'monthly' ? 'Dettaglio Mensile' : 'Dettaglio Annuale'}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4289" data-mp="30rb6nn3x7beq">
              {objectivePeriodType === 'monthly' ? <Input label="Mese" type="month" className="col-span-full" data-id="element-4290" data-mp="3aj77h5xe5fmn" /> : <Select label="Anno" className="col-span-full" options={[{
              value: '2024',
              label: '2024'
            }, {
              value: '2025',
              label: '2025'
            }, {
              value: '2026',
              label: '2026'
            }, {
              value: '2027',
              label: '2027'
            }, {
              value: '2028',
              label: '2028'
            }, {
              value: '2029',
              label: '2029'
            }, {
              value: '2030',
              label: '2030'
            }]} data-id="element-4291" data-mp="2d4be8x2m77nw" />}

              <Input label="Obiettivo preventivi (€)" type="number" data-id="element-4292" data-mp="3tobz0ncskl06" />
              <Input label="Obiettivo fatturato (€)" type="number" data-id="element-4293" data-mp="3oozdq71kjlzp" />
              <Input label="Importo + fatturato (€)" type="number" data-id="element-4294" data-mp="3opr9piz2vlqi" />
              <Input label="Importo di fatturato (€)" type="number" data-id="element-4295" data-mp="3f0bygtghbe6d" />
              <Input label="% da incassare rispetto al fatturato + proformato" type="number" endContent={<span className="text-slate-500 font-medium" data-id="element-4297" data-mp="362i3pmyd4slb">%</span>} className="col-span-full" data-id="element-4296" data-mp="3k06pqy4625ry" />
            </div>
          </section>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-4298" data-mp="1ns1vracf73w">
            <Button variant="flat" color="default" onClick={() => setShowObjectivesModal(false)} data-id="element-4299" data-mp="6ltmignf3ehh">
              Annulla
            </Button>
            <Button onClick={() => setShowObjectivesModal(false)} data-id="element-4300" data-mp="3t0czc8wqullu">Salva</Button>
          </div>
        </div>
      </Modal>

      {/* Deadline View Modal */}
      <Modal isOpen={showDeadlineViewModal} onClose={() => setShowDeadlineViewModal(false)} title="Dettaglio scadenza" size="md" data-id="element-4301" data-mp="kj8hs20cqghd">
        {selectedDeadline && <div className="space-y-6" data-id="element-4302" data-mp="2a03xnk1w482r">
            <div className="grid grid-cols-2 gap-4" data-id="element-4303" data-mp="2545gagezbqbu">
              <div data-id="element-4304" data-mp="251ascewrbndo">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4305" data-mp="2js09dxwwfwpg">
                  Titolo
                </span>
                <p className="font-medium" data-id="element-4306" data-mp="2eu86k8upuq6h">{selectedDeadline.title}</p>
              </div>
              <div data-id="element-4307" data-mp="1r2t0c4ztjopy">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4308" data-mp="1r4dw1xxvddpe">
                  Priorità
                </span>
                <div className="mt-1" data-id="element-4309" data-mp="1m6m6hakp63od">
                  <Badge variant={selectedDeadline.priority === 'high' ? 'danger' : selectedDeadline.priority === 'medium' ? 'warning' : 'info'} data-id="element-4310" data-mp="212bkomjn5cij">
                    {selectedDeadline.priority === 'high' ? 'Alta' : selectedDeadline.priority === 'medium' ? 'Media' : 'Bassa'}
                  </Badge>
                </div>
              </div>
              <div data-id="element-4311" data-mp="1esbra7fsbj0x">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4312" data-mp="1ersc2yhir8ql">
                  Cliente
                </span>
                <p className="font-medium" data-id="element-4313" data-mp="1jlwer5jr4ulv">{selectedDeadline.client}</p>
              </div>
              <div data-id="element-4314" data-mp="1oliubln1ymxu">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4315" data-mp="1skhvqw9ks1kj">
                  Data
                </span>
                <p className="font-medium" data-id="element-4316" data-mp="1xkw5ciqtrjko">{selectedDeadline.date}</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-4317" data-mp="22hw92n07ca8m">
              <Button variant="flat" color="default" onClick={() => setShowDeadlineViewModal(false)} data-id="element-4318" data-mp="36mvycmmk3ck">
                Chiudi
              </Button>
              <Button onClick={() => {
            setShowDeadlineViewModal(false);
            setShowDeadlineEditModal(true);
          }} startContent={<Edit className="h-4 w-4" data-id="element-4320" data-mp="3n06xau0k0qh6" />} data-id="element-4319" data-mp="mgovw2prwrs">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Deadline Edit Modal */}
      <Modal isOpen={showDeadlineEditModal} onClose={() => setShowDeadlineEditModal(false)} title="Modifica scadenza" size="md" data-id="element-4321" data-mp="jkjby5bpbjmy">
        {selectedDeadline && <div className="space-y-6" data-id="element-4322" data-mp="9lajjgwhid2p">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4323" data-mp="9k8ycjlr8a79">
              <Input label="Titolo" defaultValue={selectedDeadline.title} required data-id="element-4324" data-mp="4lz2e6s7h5kl" />
              <Input label="Cliente" defaultValue={selectedDeadline.client} required data-id="element-4325" data-mp="2u9yb8r2wdlxb" />
              <Input label="Data" type="date" required data-id="element-4326" data-mp="1p6sgek0o9dwe" />
              <Select label="Priorità" defaultValue={selectedDeadline.priority} options={[{
            value: 'high',
            label: 'Alta'
          }, {
            value: 'medium',
            label: 'Media'
          }, {
            value: 'low',
            label: 'Bassa'
          }]} required data-id="element-4327" data-mp="1kf0p279oz457" />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-4328" data-mp="1y5434ayf3rjk">
              <Button variant="flat" color="default" onClick={() => setShowDeadlineEditModal(false)} data-id="element-4329" data-mp="1u3sd44bmt4au">
                Annulla
              </Button>
              <Button onClick={() => setShowDeadlineEditModal(false)} data-id="element-4330" data-mp="iovmwr3po91w">
                Salva
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} title="Conferma eliminazione" size="sm" data-id="element-4331" data-mp="2yf54sqkvbuqo">
        <div className="space-y-6" data-id="element-4332" data-mp="384oqs53vo4pr">
          <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-4333" data-mp="385q3ee0jkz9l">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-4334" data-mp="3d2qc6wos0g4e" />
            <p className="text-sm text-red-700" data-id="element-4335" data-mp="3i1tei9ddg88o">
              Attenzione: questa azione è irreversibile. La scadenza verrà
              eliminata definitivamente.
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-4" data-id="element-4336" data-mp="3r3j73iizpd81">
            <Button variant="flat" color="default" onClick={() => setShowDeleteConfirm(false)} data-id="element-4337" data-mp="3r16nz21hwwte">
              Annulla
            </Button>
            <Button color="danger" onClick={() => setShowDeleteConfirm(false)} data-id="element-4338" data-mp="1wrcklnknba0f">
              Elimina
            </Button>
          </div>
        </div>
      </Modal>

      {/* Activity Detail Modal */}
      <ActivityDetailModal isOpen={showActivityDetail} onClose={() => setShowActivityDetail(false)} activity={selectedActivity} onEdit={handleEditActivity} onStepComplete={handleStepComplete} data-id="element-4339" data-mp="2v35pt4ytzua2" />
    </Layout>;
}